import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="gradient-hero py-12 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4" variant="outline">
              Legal
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">Last updated: January 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                  <p className="text-muted-foreground">
                    At wealthcalc ("we," "our," or "us"), we take your privacy seriously. This Privacy Policy explains
                    how we collect, use, and protect your information when you use our website and financial
                    calculators.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Information We Collect</h2>
                  <h3 className="text-xl font-semibold text-foreground mb-3">2.1 Information You Provide</h3>
                  <p className="text-muted-foreground mb-4">
                    When you use our calculators, all calculations are performed locally in your browser. We do not
                    collect, store, or transmit any financial data you enter into our tools.
                  </p>
                  <h3 className="text-xl font-semibold text-foreground mb-3">2.2 Automatically Collected Information</h3>
                  <p className="text-muted-foreground">
                    We may collect certain information automatically when you visit our website, including:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>IP address (anonymized)</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. How We Use Your Information</h2>
                  <p className="text-muted-foreground mb-3">We use the automatically collected information to:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Improve our website and user experience</li>
                    <li>Analyze usage patterns and trends</li>
                    <li>Detect and prevent technical issues</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Cookies and Tracking</h2>
                  <p className="text-muted-foreground mb-3">
                    We use cookies and similar tracking technologies to enhance your experience. These include:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                    <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements</li>
                  </ul>
                  <p className="text-muted-foreground mt-3">
                    You can control cookie preferences through your browser settings.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Third-Party Services</h2>
                  <p className="text-muted-foreground mb-3">We may use third-party services for:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li><strong>Google Analytics:</strong> To analyze website traffic and usage</li>
                    <li><strong>Google AdSense:</strong> To display relevant advertisements</li>
                  </ul>
                  <p className="text-muted-foreground mt-3">
                    These services have their own privacy policies and may collect information as described in their
                    respective policies.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Data Security</h2>
                  <p className="text-muted-foreground">
                    We implement appropriate technical and organizational measures to protect your information. However,
                    no method of transmission over the internet is 100% secure. Since all calculations happen in your
                    browser and we don't store financial data, your sensitive information remains on your device.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Your Rights</h2>
                  <p className="text-muted-foreground mb-3">You have the right to:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Access the information we hold about you</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion of your information</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Disable cookies through your browser settings</li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Children's Privacy</h2>
                  <p className="text-muted-foreground">
                    Our services are not intended for children under 13 years of age. We do not knowingly collect
                    information from children under 13.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">
                    9. Changes to This Privacy Policy
                  </h2>
                  <p className="text-muted-foreground">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting
                    the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">10. Contact Us</h2>
                  <p className="text-muted-foreground">
                    If you have any questions about this Privacy Policy, please contact us at:
                    <br />
                    <strong>Email:</strong> privacy@wealthcalc.online
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
